import { useCallback, useEffect, useRef, useState } from 'react';

import type { DoorVisitReportApi, DoorVisitReportRun } from '../doorVisitReportApi';

export const DOOR_VISIT_REPORT_RUNS_DEFAULT_ROWS_PER_PAGE = 25;
export const DOOR_VISIT_REPORT_RUNS_ROWS_PER_PAGE_OPTIONS = [25, 50, 100] as const;

export type UseDoorVisitReportRunsOptions = {
  api: Pick<DoorVisitReportApi, 'getReportRuns'>;
};

export type UseDoorVisitReportRunsResult = {
  items: DoorVisitReportRun[];
  total: number;
  loading: boolean;
  refreshing: boolean;
  error: string | null;
  refreshError: string | null;
  page: number;
  rowsPerPage: number;
  successfulManualRefreshVersion: number;
  setPage: (page: number) => void;
  setRowsPerPage: (rowsPerPage: number) => void;
  updateReport: (report: DoorVisitReportRun) => void;
  removeReport: (reportId: string) => void;
  refresh: () => void;
};

const normalizePage = (page: number): number => (Number.isSafeInteger(page) && page > 0 ? page : 0);

const normalizeRowsPerPage = (rowsPerPage: number): number =>
  DOOR_VISIT_REPORT_RUNS_ROWS_PER_PAGE_OPTIONS.includes(rowsPerPage as 25 | 50 | 100)
    ? rowsPerPage
    : DOOR_VISIT_REPORT_RUNS_DEFAULT_ROWS_PER_PAGE;

export const useDoorVisitReportRuns = ({ api }: UseDoorVisitReportRunsOptions): UseDoorVisitReportRunsResult => {
  const [items, setItems] = useState<DoorVisitReportRun[] | null>(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPageState] = useState(DOOR_VISIT_REPORT_RUNS_DEFAULT_ROWS_PER_PAGE);
  const [manualRefreshRequestVersion, setManualRefreshRequestVersion] = useState(0);
  const [successfulManualRefreshVersion, setSuccessfulManualRefreshVersion] = useState(0);
  const lastStartedManualRefreshVersion = useRef(0);
  const dataVersion = useRef(0);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    const hasRows = items !== null;
    const isManualRefresh = manualRefreshRequestVersion > lastStartedManualRefreshVersion.current;
    const requestDataVersion = dataVersion.current;
    if (isManualRefresh) lastStartedManualRefreshVersion.current = manualRefreshRequestVersion;

    if (hasRows) setRefreshing(true);
    else setLoading(true);

    void api.getReportRuns({ first: rowsPerPage, offset: page * rowsPerPage }, { signal: controller.signal }).then(result => {
      if (!active || controller.signal.aborted || requestDataVersion !== dataVersion.current) return;
      setItems(result.items);
      setTotal(result.total);
      setError(null);
      setRefreshError(null);
      if (isManualRefresh) setSuccessfulManualRefreshVersion(version => version + 1);
    }).catch(() => {
      if (!active || controller.signal.aborted || requestDataVersion !== dataVersion.current) return;
      if (hasRows) setRefreshError('Could not refresh report runs. Try again.');
      else setError('Could not load report runs. Try again.');
    }).finally(() => {
      if (!active || controller.signal.aborted || requestDataVersion !== dataVersion.current) return;
      setLoading(false);
      setRefreshing(false);
    });

    return () => {
      active = false;
      controller.abort();
    };
  }, [api, manualRefreshRequestVersion, page, rowsPerPage]);

  const setRowsPerPage = useCallback((nextRowsPerPage: number): void => {
    setRowsPerPageState(normalizeRowsPerPage(nextRowsPerPage));
    setPage(0);
  }, []);

  const updateReport = useCallback((nextReport: DoorVisitReportRun): void => {
    setItems(currentItems =>
      currentItems === null
        ? currentItems
        : currentItems.map(report => (report.id === nextReport.id ? nextReport : report)),
    );
  }, []);

  const removeReport = useCallback((reportId: string): void => {
    // A request begun before deletion must not restore the removed report after
    // the server has accepted the mutation. The next manual refresh reads a new
    // authoritative page normally.
    dataVersion.current += 1;
    setItems(currentItems => currentItems === null ? currentItems : currentItems.filter(report => report.id !== reportId));
    setTotal(currentTotal => Math.max(0, currentTotal - 1));
    setRefreshing(false);
  }, []);

  return {
    items: items ?? [],
    total,
    loading,
    refreshing,
    error,
    refreshError,
    page,
    rowsPerPage,
    successfulManualRefreshVersion,
    setPage: nextPage => setPage(normalizePage(nextPage)),
    setRowsPerPage,
    updateReport,
    removeReport,
    refresh: () => setManualRefreshRequestVersion(version => version + 1),
  };
};
